import { memo, useCallback, useMemo } from 'react';

import { Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { withRouter } from 'react-router-dom';
import './ApiLogTableList.less';

import { ApiLog } from '../../../../../models/api-log';
import { useAppDispatch } from '../../../../../store';
import { paginateApiLogs } from '../../../../../store/features/api-log/apiLogsSlice';
import { formatDate, propsAreEqual } from '../../../../../util';

const { Text } = Typography;

interface ApiLogTableListProps {
  apiLogs: ApiLog[];
  loading: boolean;
  totalCount: number;
  userId: string;
}

const ApiLogTableList = ({
  apiLogs,
  loading,
  totalCount,
  userId,
}: ApiLogTableListProps) => {
  const dispatch = useAppDispatch();

  const columns: ColumnsType<ApiLog> = useMemo(
    () => [
      {
        title: 'Time stamp',
        dataIndex: 'created_at',
        key: 'created_at',
        render: (created_at) => (
          <Text style={{ fontWeight: 'bold' }}>{formatDate(created_at)}</Text>
        ),
      },
      {
        title: 'Plain Language Request',
        dataIndex: 'path',
        key: 'path',
        render: (path: string) => (
          <Text ellipsis style={{ maxWidth: '250px' }}>
            {path}
          </Text>
        ),
      },
      {
        title: 'API Call/Session/Request',
        dataIndex: 'query_string',
        key: 'query_string',
        render: (query_string: string) => (
          <Text ellipsis style={{ maxWidth: '400px' }}>
            {query_string || 'N/A'}
          </Text>
        ),
      },
      {
        title: 'IP',
        dataIndex: 'request_headers',
        key: 'request_headers',
        render: (request_headers: string) => {
          const headers = JSON.parse(request_headers);
          const ip = headers['cf-connecting-ip'];
          return (
            <Text ellipsis style={{ maxWidth: '150px' }}>
              {ip}
            </Text>
          );
        },
      },
    ],
    []
  );

  const paginate = useCallback(
    (page: number, pageSize: number) =>
      dispatch(paginateApiLogs({ page, pageSize, userId })),
    [dispatch, userId]
  );

  return (
    <Table
      loading={loading}
      locale={{ emptyText: <></> }}
      rowKey={(user) => `${user.uuid}-row-key`}
      className="apiLogs-table-list"
      pagination={{
        defaultPageSize: 20,
        total: totalCount,
        onChange: paginate,
        showSizeChanger: true,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} items`,
      }}
      columns={columns}
      dataSource={apiLogs}
    />
  );
};

export default withRouter<any, any>(memo(ApiLogTableList, propsAreEqual));
